import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw, X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../../lib/utils";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { inventoryManagementService } from "../../../services/inventoryManagementService";

const PAGE_SIZE = 10;

const WARRANTY_TYPE_OPTIONS = [
  { value: "Under_Warranty", labelKey: "inventory.warrantyUnderWarranty" },
  { value: "No_Warranty", labelKey: "inventory.warrantyNoWarranty" },
  { value: "Chargeable", labelKey: "inventory.warrantyChargeable" },
  { value: "Non_Chargeable", labelKey: "inventory.warrantyNonChargeable" },
];

const selectTriggerClass =
  "h-10 rounded-xl border border-(--color-text-border) bg-white text-xs font-semibold focus:ring-accent w-full disabled:opacity-50";

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function mergeUniqueById(existing, incoming) {
  const map = new Map();
  [...(existing || []), ...(incoming || [])].forEach((item) => {
    if (item?.id == null || item.id === "") return;
    map.set(String(item.id), item);
  });
  return Array.from(map.values());
}

function FieldLabel({ children, required = false, loading = false }) {
  return (
    <label className="text-[11px] font-bold uppercase tracking-wider per-file-hint flex items-center gap-1 min-h-[18px]">
      <span>{children}</span>
      {required ? <span className="text-rose-500">*</span> : null}
      {loading ? <RefreshCw className="h-3 w-3 animate-spin text-primary" /> : null}
    </label>
  );
}

function FieldError({ message }) {
  if (!message) return null;
  return <p className="text-[11px] text-rose-500 font-semibold mt-1">{message}</p>;
}

function ReadOnlyValue({ value }) {
  return (
    <div className="h-10 px-3 bg-gray-50 border border-(--color-text-border) rounded-xl flex items-center text-xs font-bold text-foreground font-mono">
      {value}
    </div>
  );
}

function formatApiQuantity(value) {
  if (value == null || value === "") return "—";
  return String(value);
}

function toQtyNumber(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatBatchOptionLabel(row, party) {
  const rawPrice = row?.purchasePricePerUnit;
  const price =
    rawPrice == null || rawPrice === ""
      ? "—"
      : Number.isFinite(Number(rawPrice))
        ? Number(rawPrice).toFixed(2)
        : String(rawPrice);
  const vendorInvoice =
    row?.vendorInvoiceNumber != null &&
    String(row.vendorInvoiceNumber).trim() !== ""
      ? String(row.vendorInvoiceNumber).trim()
      : "";
  const customerInvoice =
    row?.customerInvoiceId != null && String(row.customerInvoiceId).trim() !== ""
      ? String(row.customerInvoiceId).trim()
      : "";
  // Prefer party-specific invoice; fall back so purchase batches still show vendor invoice.
  const invoice =
    party === "customer"
      ? customerInvoice || vendorInvoice || "—"
      : party === "vendor"
        ? vendorInvoice || customerInvoice || "—"
        : vendorInvoice || customerInvoice || "—";
  return `${row?.batchNumber || "—"} | ${price} | ${invoice}`;
}

/**
 * @param {{
 *   open: boolean,
 *   onOpenChange: (open: boolean) => void,
 *   onSuccess: () => void,
 *   transferOption: {
 *     value: string,
 *     labelKey: string,
 *     party: "warehouse" | "vendor" | "customer",
 *     usesDefectiveQty?: boolean,
 *   },
 * }} props
 */
export function StockTransferModal({
  open,
  onOpenChange,
  onSuccess,
  transferOption,
}) {
  const { t } = useTranslation();
  const party = transferOption?.party;
  const transferType = transferOption?.value || "";
  const usesDefectiveQty = Boolean(transferOption?.usesDefectiveQty);
  const isWarehouseTransfer = party === "warehouse";
  const isVendorTransfer = party === "vendor";
  const isCustomerTransfer = party === "customer";

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const [warehouses, setWarehouses] = useState([]);
  const [warehousePage, setWarehousePage] = useState(0);
  const [warehouseHasMore, setWarehouseHasMore] = useState(false);
  const [loadingWarehouses, setLoadingWarehouses] = useState(true);
  const [loadingMoreWarehouses, setLoadingMoreWarehouses] = useState(false);
  const warehouseFetchLock = useRef(false);

  const [inventoryRows, setInventoryRows] = useState([]);
  const [productPage, setProductPage] = useState(0);
  const [productHasMore, setProductHasMore] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingMoreProducts, setLoadingMoreProducts] = useState(false);
  const productSession = useRef(0);

  const [sourceWarehouseId, setSourceWarehouseId] = useState("");
  const [productId, setProductId] = useState("");
  const [batchKey, setBatchKey] = useState("");
  const [batchStock, setBatchStock] = useState(null);
  const [loadingBatchStock, setLoadingBatchStock] = useState(false);
  const [batchStockError, setBatchStockError] = useState("");
  const [transferQty, setTransferQty] = useState(0);
  const [destinationWarehouseId, setDestinationWarehouseId] = useState("");
  const [warrantyType, setWarrantyType] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [vendors, setVendors] = useState([]);
  const [vendorPage, setVendorPage] = useState(0);
  const [vendorHasMore, setVendorHasMore] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingMoreVendors, setLoadingMoreVendors] = useState(false);
  const vendorFetchLock = useRef(false);
  const [customerId, setCustomerId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [customerPage, setCustomerPage] = useState(0);
  const [customerHasMore, setCustomerHasMore] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingMoreCustomers, setLoadingMoreCustomers] = useState(false);
  const customerFetchLock = useRef(false);
  const [customerAddressId, setCustomerAddressId] = useState("");
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [loadingCustomerAddresses, setLoadingCustomerAddresses] = useState(false);
  const [customerMobileId, setCustomerMobileId] = useState("");
  const [customerMobiles, setCustomerMobiles] = useState([]);
  const [loadingCustomerMobiles, setLoadingCustomerMobiles] = useState(false);
  const [vendorAddressId, setVendorAddressId] = useState("");
  const [vendorAddresses, setVendorAddresses] = useState([]);
  const [vendorMobileId, setVendorMobileId] = useState("");
  const [vendorMobiles, setVendorMobiles] = useState([]);
  const [loadingVendorParty, setLoadingVendorParty] = useState(false);
  const [transferDate, setTransferDate] = useState(todayIsoDate);

  const loadWarehouses = useCallback(async (page, { append = false } = {}) => {
    if (warehouseFetchLock.current) return;
    warehouseFetchLock.current = true;
    if (append) setLoadingMoreWarehouses(true);
    else setLoadingWarehouses(true);

    try {
      const result = await inventoryManagementService.getTransferWarehouses({
        page,
        size: PAGE_SIZE,
      });
      setWarehouses((prev) =>
        append ? mergeUniqueById(prev, result.content) : result.content || []
      );
      setWarehousePage(result.page ?? page);
      setWarehouseHasMore(Boolean(result.hasMore));
    } catch (err) {
      console.error("Failed to load warehouses list:", err);
      if (!append) setWarehouses([]);
    } finally {
      setLoadingWarehouses(false);
      setLoadingMoreWarehouses(false);
      warehouseFetchLock.current = false;
    }
  }, []);

  const loadVendors = useCallback(async (page, { append = false } = {}) => {
    if (vendorFetchLock.current) return;
    vendorFetchLock.current = true;
    if (append) setLoadingMoreVendors(true);
    else setLoadingVendors(true);

    try {
      const result = await inventoryManagementService.getTransferVendors({
        page,
        size: PAGE_SIZE,
      });
      setVendors((prev) =>
        append ? mergeUniqueById(prev, result.content) : result.content || []
      );
      setVendorPage(result.page ?? page);
      setVendorHasMore(Boolean(result.hasMore));
    } catch (err) {
      console.error("Failed to load active vendors:", err);
      if (!append) setVendors([]);
      setVendorHasMore(false);
    } finally {
      setLoadingVendors(false);
      setLoadingMoreVendors(false);
      vendorFetchLock.current = false;
    }
  }, []);

  const loadCustomers = useCallback(async (page, { append = false } = {}) => {
    if (customerFetchLock.current) return;
    customerFetchLock.current = true;
    if (append) setLoadingMoreCustomers(true);
    else setLoadingCustomers(true);

    try {
      const result = await inventoryManagementService.getAllocationCustomers({
        page,
        size: PAGE_SIZE,
      });
      setCustomers((prev) =>
        append ? mergeUniqueById(prev, result.content) : result.content || []
      );
      setCustomerPage(result.page ?? page);
      setCustomerHasMore(Boolean(result.hasMore));
    } catch (err) {
      console.error("Failed to load customers:", err);
      if (!append) setCustomers([]);
      setCustomerHasMore(false);
    } finally {
      setLoadingCustomers(false);
      setLoadingMoreCustomers(false);
      customerFetchLock.current = false;
    }
  }, []);

  const loadProducts = useCallback(async (warehouseId, page, { append = false, session } = {}) => {
    if (!warehouseId) return;
    if (append) setLoadingMoreProducts(true);
    else setLoadingProducts(true);

    try {
      const result = await inventoryManagementService.getTransferProducts({
        warehouseLocationId: warehouseId,
        page,
        size: PAGE_SIZE,
      });
      if (session != null && session !== productSession.current) return;
      setInventoryRows((prev) =>
        append ? [...prev, ...(result.content || [])] : result.content || []
      );
      setProductPage(result.page ?? page);
      setProductHasMore(Boolean(result.hasMore));
    } catch (err) {
      console.error("Failed to load transfer products:", err);
      if (session == null || session === productSession.current) {
        if (!append) setInventoryRows([]);
        setProductHasMore(false);
      }
    } finally {
      if (session == null || session === productSession.current) {
        setLoadingProducts(false);
        setLoadingMoreProducts(false);
      }
    }
  }, []);

  useEffect(() => {
    loadWarehouses(0);
  }, [loadWarehouses]);

  useEffect(() => {
    if (!isVendorTransfer) return;
    loadVendors(0);
  }, [isVendorTransfer, loadVendors]);

  useEffect(() => {
    if (!isCustomerTransfer) return;
    loadCustomers(0);
  }, [isCustomerTransfer, loadCustomers]);

  useEffect(() => {
    setCustomerAddressId("");
    setCustomerAddresses([]);
    setCustomerMobileId("");
    setCustomerMobiles([]);

    if (!isCustomerTransfer || !customerId) {
      setLoadingCustomerAddresses(false);
      setLoadingCustomerMobiles(false);
      return undefined;
    }

    let active = true;
    setLoadingCustomerAddresses(true);
    setLoadingCustomerMobiles(true);

    inventoryManagementService
      .getTransferCustomerLocations({ customerId })
      .then((rows) => {
        if (!active) return;
        const list = rows || [];
        setCustomerAddresses(list);
        if (list.length > 0) setCustomerAddressId(String(list[0].id));
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to load customer locations:", err);
        setCustomerAddresses([]);
      })
      .finally(() => {
        if (active) setLoadingCustomerAddresses(false);
      });

    inventoryManagementService
      .getTransferCustomerMobiles({ customerId })
      .then((rows) => {
        if (!active) return;
        const list = rows || [];
        setCustomerMobiles(list);
        if (list.length > 0) setCustomerMobileId(String(list[0].id));
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to load customer contacts:", err);
        setCustomerMobiles([]);
      })
      .finally(() => {
        if (active) setLoadingCustomerMobiles(false);
      });

    return () => {
      active = false;
    };
  }, [isCustomerTransfer, customerId]);

  useEffect(() => {
    setVendorAddressId("");
    setVendorAddresses([]);
    setVendorMobileId("");
    setVendorMobiles([]);

    if (!isVendorTransfer || !vendorId) {
      setLoadingVendorParty(false);
      return undefined;
    }

    let active = true;
    setLoadingVendorParty(true);

    inventoryManagementService
      .getTransferVendorPartyDetails(vendorId)
      .then((result) => {
        if (!active) return;
        const addresses = result?.addresses || [];
        const mobiles = result?.mobiles || [];
        setVendorAddresses(addresses);
        setVendorMobiles(mobiles);
        if (addresses.length > 0) setVendorAddressId(String(addresses[0].id));
        if (mobiles.length > 0) setVendorMobileId(String(mobiles[0].id));
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to load vendor address/mobile:", err);
        setVendorAddresses([]);
        setVendorMobiles([]);
      })
      .finally(() => {
        if (active) setLoadingVendorParty(false);
      });

    return () => {
      active = false;
    };
  }, [isVendorTransfer, vendorId]);

  useEffect(() => {
    const session = ++productSession.current;
    setProductId("");
    setBatchKey("");
    setBatchStock(null);
    setBatchStockError("");
    setTransferQty(0);
    setInventoryRows([]);
    setProductPage(0);
    setProductHasMore(false);

    if (!sourceWarehouseId) return undefined;
    loadProducts(sourceWarehouseId, 0, { session });
    return () => {
      productSession.current += 1;
    };
  }, [sourceWarehouseId, loadProducts]);

  const products = useMemo(() => {
    const map = new Map();
    for (const row of inventoryRows) {
      if (!row?.productId || map.has(row.productId)) continue;
      map.set(row.productId, {
        id: row.productId,
        name: row.productName || row.name,
      });
    }
    return Array.from(map.values());
  }, [inventoryRows]);

  const productBatches = useMemo(
    () =>
      inventoryRows.filter(
        (row) => String(row.productId) === String(productId) && row.batchNumber
      ),
    [inventoryRows, productId]
  );

  const selectedBatchObj = useMemo(
    () => productBatches.find((row) => row.rowKey === batchKey) || null,
    [productBatches, batchKey]
  );

  const batchNumber = selectedBatchObj?.batchNumber || "";

  useEffect(() => {
    if (!sourceWarehouseId || !productId || !batchNumber) {
      setBatchStock(null);
      setBatchStockError("");
      setLoadingBatchStock(false);
      return undefined;
    }

    let active = true;
    setLoadingBatchStock(true);
    setBatchStock(null);
    setBatchStockError("");

    inventoryManagementService
      .getTransferBatchStock({
        productId,
        warehouseLocationId: sourceWarehouseId,
        batchNumber,
      })
      .then((res) => {
        if (!active) return;
        if (!res) {
          setBatchStock(null);
          setBatchStockError(t("validation.stockTransferSaveError"));
        } else {
          setBatchStock(res);
        }
        setLoadingBatchStock(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to load batch stock:", err);
        const apiMessage =
          err?.message ||
          (typeof err?.data === "string" ? err.data : null) ||
          err?.data?.message ||
          null;
        setBatchStock(null);
        setBatchStockError(
          apiMessage && String(apiMessage).trim() && String(apiMessage) !== "[object Object]"
            ? String(apiMessage)
            : t("validation.stockTransferSaveError")
        );
        setLoadingBatchStock(false);
      });

    return () => {
      active = false;
    };
  }, [sourceWarehouseId, productId, batchNumber, t]);

  const availableQty = batchStock ? batchStock.availableQuantity : null;
  const defectiveQty = batchStock ? batchStock.defectiveQuantity : null;
  const availableQtyNumber = toQtyNumber(availableQty);
  const defectiveQtyNumber = toQtyNumber(defectiveQty);
  const maxTransferQty = usesDefectiveQty ? defectiveQtyNumber : availableQtyNumber;
  const stockReady = Boolean(batchStock) && !loadingBatchStock && !batchStockError;

  const errors = useMemo(() => {
    const errs = {};
    if (!sourceWarehouseId) {
      errs.sourceWarehouseId = t("validation.fieldRequired", {
        field: t("inventory.warehouse"),
      });
    }
    if (!productId) {
      errs.productId = t("validation.fieldRequired", { field: t("inventory.product") });
    }
    if (!batchNumber) {
      errs.batchNumber = t("validation.fieldRequired", { field: t("inventory.batchNumber") });
    } else if (!stockReady || maxTransferQty == null) {
      errs.transferQty = batchStockError || t("common.loading");
    } else if (usesDefectiveQty && defectiveQtyNumber <= 0) {
      errs.transferQty = t("inventory.noDefectiveQuantity");
    } else if (transferQty <= 0) {
      errs.transferQty = t("validation.qtyMustBeGreaterThanZero", {
        field: t("formActions.transferQuantity"),
      });
    } else if (transferQty > maxTransferQty) {
      errs.transferQty = usesDefectiveQty
        ? t("validation.transferQtyExceedsDefective")
        : t("validation.transferQtyExceedsAvailable");
    }
    if (isWarehouseTransfer) {
      if (!destinationWarehouseId) {
        errs.destinationWarehouseId = t("validation.fieldRequired", {
          field: t("inventory.destinationWarehouse"),
        });
      } else if (destinationWarehouseId === sourceWarehouseId) {
        errs.destinationWarehouseId = t("validation.sourceDestMustDiffer");
      }
    } else if (isVendorTransfer && !vendorId) {
      errs.vendorId = t("validation.fieldRequired", {
        field: t("inventory.vendorName"),
      });
    } else if (isCustomerTransfer && !customerId) {
      errs.customerId = t("validation.fieldRequired", {
        field: t("inventory.customerName"),
      });
    }
    if (!transferDate) {
      errs.transferDate = t("validation.fieldRequired", {
        field: t("dateTime.transferDate"),
      });
    }
    return errs;
  }, [
    sourceWarehouseId,
    productId,
    batchNumber,
    stockReady,
    batchStockError,
    usesDefectiveQty,
    defectiveQtyNumber,
    transferQty,
    maxTransferQty,
    isWarehouseTransfer,
    isVendorTransfer,
    isCustomerTransfer,
    destinationWarehouseId,
    vendorId,
    customerId,
    transferDate,
    t,
  ]);

  const handleWarehouseScroll = (e) => {
    if (!warehouseHasMore || loadingMoreWarehouses || loadingWarehouses) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
      loadWarehouses(warehousePage + 1, { append: true });
    }
  };

  const handleProductScroll = (e) => {
    if (!sourceWarehouseId || !productHasMore || loadingMoreProducts || loadingProducts) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
      loadProducts(sourceWarehouseId, productPage + 1, {
        append: true,
        session: productSession.current,
      });
    }
  };

  const handleVendorScroll = (e) => {
    if (!isVendorTransfer || !vendorHasMore || loadingMoreVendors || loadingVendors) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
      loadVendors(vendorPage + 1, { append: true });
    }
  };

  const handleCustomerScroll = (e) => {
    if (!isCustomerTransfer || !customerHasMore || loadingMoreCustomers || loadingCustomers) {
      return;
    }
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
      loadCustomers(customerPage + 1, { append: true });
    }
  };

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen && submitting) return;
    onOpenChange(nextOpen);
  };

  const handleSave = (e) => {
    e.preventDefault();
    setAttemptedSubmit(true);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    setSubmitError("");

    const sourceWarehouse = warehouses.find(
      (w) => String(w.id) === String(sourceWarehouseId)
    );
    const destinationWarehouse = warehouses.find(
      (w) => String(w.id) === String(destinationWarehouseId)
    );
    const vendor = vendors.find((v) => String(v.id) === String(vendorId));
    const customer = customers.find((c) => String(c.id) === String(customerId));
    const customerAddress = customerAddresses.find(
      (row) => String(row.id) === String(customerAddressId)
    );
    const customerMobile = customerMobiles.find(
      (row) => String(row.id) === String(customerMobileId)
    );
    const vendorAddress = vendorAddresses.find(
      (row) => String(row.id) === String(vendorAddressId)
    );
    const vendorMobile = vendorMobiles.find(
      (row) => String(row.id) === String(vendorMobileId)
    );

    let toName = "";
    let challanToAddress = null;
    let challanToMobile = null;
    const payload = {
      productId: Number(productId),
      batchNumber,
      transferQuantity: Number(transferQty),
      purchasePricePerUnit: Number(selectedBatchObj?.purchasePricePerUnit ?? 0),
      transferDate,
      transferType,
      warrantyType: warrantyType || null,
      invoiceNumber: (() => {
        const vendorInvoice =
          selectedBatchObj?.vendorInvoiceNumber != null &&
          String(selectedBatchObj.vendorInvoiceNumber).trim() !== ""
            ? String(selectedBatchObj.vendorInvoiceNumber).trim()
            : null;
        const customerInvoice =
          selectedBatchObj?.customerInvoiceId != null &&
          String(selectedBatchObj.customerInvoiceId).trim() !== ""
            ? String(selectedBatchObj.customerInvoiceId).trim()
            : null;
        if (isCustomerTransfer) return customerInvoice || vendorInvoice;
        if (isVendorTransfer) return vendorInvoice || customerInvoice;
        return vendorInvoice || customerInvoice;
      })(),
      fromName: sourceWarehouse?.name || "",
      sourceWarehouseId: Number(sourceWarehouseId),
    };

    if (isWarehouseTransfer) {
      toName = destinationWarehouse?.name || "";
      payload.destinationWarehouseId = Number(destinationWarehouseId);
    } else if (isVendorTransfer) {
      toName = vendor?.name || "";
      payload.vendorId = Number(vendorId);
      challanToAddress = vendorAddress?.address || vendorAddress?.label || null;
      challanToMobile = vendorMobile?.mobile || vendorMobile?.label || null;
    } else if (isCustomerTransfer) {
      toName = customer?.name || "";
      payload.customerId = Number(customerId);
      challanToAddress =
        customerAddress?.address || customerAddress?.label || null;
      challanToMobile = customerMobile?.mobile || customerMobile?.label || null;
    }
    payload.toName = toName;
    if (isVendorTransfer || isCustomerTransfer) {
      payload.challanTo = toName;
      payload.challanToAddress = challanToAddress;
      payload.challanToMobile = challanToMobile;
    }

    inventoryManagementService
      .createStockTransfer(payload)
      .then(() => {
        setSubmitting(false);
        onSuccess();
      })
      .catch((err) => {
        console.error("Failed to complete stock transfer:", err);
        const apiMessage =
          err?.message ||
          (typeof err?.data === "string" ? err.data : null) ||
          err?.data?.message ||
          null;
        setSubmitError(
          apiMessage && String(apiMessage).trim() && String(apiMessage) !== "[object Object]"
            ? String(apiMessage)
            : t("validation.stockTransferSaveError")
        );
        setSubmitting(false);
      });
  };

  const productPlaceholder = !sourceWarehouseId
    ? t("inventory.selectSourceWarehouseFirst")
    : t("inventory.selectProduct");
  const showError = (key) => (attemptedSubmit ? errors[key] : "");
  const title = transferOption ? t(transferOption.labelKey) : t("inventory.stockTransfer");

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/40 backdrop-blur-xs fixed inset-0 z-40 transition-opacity animate-in fade-in duration-200" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] sm:w-[90vw] md:w-[840px] max-h-[90vh]",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
            "p-6 flex flex-col gap-4 font-sans focus:outline-hidden",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}
        >
          <div className="flex items-center justify-between shrink-0">
            <DialogTitle className="text-[16px] font-bold text-foreground pr-4">
              {title}
            </DialogTitle>
            <DialogPrimitive.Close
              disabled={submitting}
              className="h-8 w-8 grid place-items-center rounded-lg per-file-hint hover:bg-workspace/60 hover:text-primary transition focus:outline-hidden disabled:opacity-50"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">{t("formActions.close")}</span>
            </DialogPrimitive.Close>
          </div>

          <form onSubmit={handleSave} className="flex flex-col gap-4 min-h-0">
            <div className="overflow-y-auto max-h-[62vh] pr-1">
              {submitError ? (
                <div className="mb-4 p-3 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-sm font-semibold">
                  {submitError}
                </div>
              ) : null}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4 items-start">
                <div className="space-y-1.5">
                  <FieldLabel required>{t("inventory.transferType")}</FieldLabel>
                  <div className="h-10 px-3 bg-gray-50 border border-(--color-text-border) rounded-xl flex items-center text-xs font-semibold text-foreground">
                    {title}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <FieldLabel required>{t("inventory.warehouse")}</FieldLabel>
                  <Select
                    value={sourceWarehouseId}
                    onValueChange={setSourceWarehouseId}
                    disabled={loadingWarehouses && warehouses.length === 0}
                  >
                    <SelectTrigger className={selectTriggerClass}>
                      <SelectValue placeholder={t("inventory.selectWarehouse")} />
                    </SelectTrigger>
                    <SelectContent
                      className="z-[100]"
                      viewportClassName="h-auto max-h-60 overflow-y-auto"
                      onViewportScroll={handleWarehouseScroll}
                    >
                      {warehouses.map((w) => (
                        <SelectItem key={w.id} value={w.id}>
                          {w.name}
                        </SelectItem>
                      ))}
                      {loadingMoreWarehouses ? (
                        <div className="px-2 py-1.5 text-xs per-file-hint text-center">
                          {t("common.loading")}
                        </div>
                      ) : null}
                    </SelectContent>
                  </Select>
                  <FieldError message={showError("sourceWarehouseId")} />
                </div>

                <div className="space-y-1.5">
                  <FieldLabel required loading={loadingProducts}>
                    {t("inventory.product")}
                  </FieldLabel>
                  <Select
                    value={productId}
                    onValueChange={(value) => {
                      setProductId(value);
                      setBatchKey("");
                      setBatchStock(null);
                      setBatchStockError("");
                      setTransferQty(0);
                    }}
                    disabled={!sourceWarehouseId || (loadingProducts && products.length === 0)}
                  >
                    <SelectTrigger className={selectTriggerClass}>
                      <SelectValue placeholder={productPlaceholder} />
                    </SelectTrigger>
                    <SelectContent
                      className="z-[100]"
                      viewportClassName="h-auto max-h-60 overflow-y-auto"
                      onViewportScroll={handleProductScroll}
                    >
                      {products.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                      {loadingMoreProducts ? (
                        <div className="px-2 py-1.5 text-xs per-file-hint text-center">
                          {t("common.loading")}
                        </div>
                      ) : null}
                    </SelectContent>
                  </Select>
                  {sourceWarehouseId && !loadingProducts && products.length === 0 ? (
                    <p className="text-[11px] text-rose-500 font-bold mt-1">
                      {t("inventory.noInventoryAvailable")}
                    </p>
                  ) : null}
                  <FieldError message={showError("productId")} />
                </div>

                <div className="space-y-1.5">
                  <FieldLabel required>{t("inventory.batchNumber")}</FieldLabel>
                  <Select
                    value={batchKey}
                    onValueChange={(value) => {
                      setBatchKey(value);
                      setTransferQty(0);
                    }}
                    disabled={!productId}
                  >
                    <SelectTrigger className={selectTriggerClass}>
                      <SelectValue
                        placeholder={
                          !productId
                            ? t("inventory.selectProductFirst")
                            : t("inventory.selectBatch")
                        }
                      />
                    </SelectTrigger>
                    <SelectContent
                      className="z-[100]"
                      viewportClassName="h-auto max-h-60 overflow-y-auto"
                      onViewportScroll={handleProductScroll}
                    >
                      {productBatches.map((b) => (
                        <SelectItem key={b.rowKey} value={b.rowKey}>
                          {formatBatchOptionLabel(b, party)}
                        </SelectItem>
                      ))}
                      {loadingMoreProducts ? (
                        <div className="px-2 py-1.5 text-xs per-file-hint text-center">
                          {t("common.loading")}
                        </div>
                      ) : null}
                    </SelectContent>
                  </Select>
                  {productId &&
                  !loadingProducts &&
                  !loadingMoreProducts &&
                  !productHasMore &&
                  productBatches.length === 0 ? (
                    <p className="text-[11px] text-rose-500 font-bold mt-1">
                      {t("inventory.noTransferableBatches")}
                    </p>
                  ) : null}
                  <FieldError message={showError("batchNumber")} />
                </div>

                <div className="space-y-1.5">
                  <FieldLabel>{t("inventory.availableQty")}</FieldLabel>
                  <ReadOnlyValue
                    value={stockReady ? formatApiQuantity(availableQty) : "—"}
                  />
                </div>

                <div className="space-y-1.5">
                  <FieldLabel>{t("inventory.defectiveQuantity")}</FieldLabel>
                  <ReadOnlyValue
                    value={stockReady ? formatApiQuantity(defectiveQty) : "—"}
                  />
                  {usesDefectiveQty &&
                  stockReady &&
                  defectiveQtyNumber != null &&
                  defectiveQtyNumber <= 0 ? (
                    <p className="text-[11px] text-rose-500 font-bold mt-1">
                      {t("inventory.noDefectiveQuantity")}
                    </p>
                  ) : null}
                </div>

                {isWarehouseTransfer ? (
                  <div className="space-y-1.5">
                    <FieldLabel required>{t("inventory.destinationWarehouse")}</FieldLabel>
                    <Select
                      value={destinationWarehouseId}
                      onValueChange={setDestinationWarehouseId}
                    >
                      <SelectTrigger className={selectTriggerClass}>
                        <SelectValue placeholder={t("inventory.selectWarehouse")} />
                      </SelectTrigger>
                      <SelectContent
                        className="z-[100]"
                        viewportClassName="h-auto max-h-60 overflow-y-auto"
                        onViewportScroll={handleWarehouseScroll}
                      >
                        {warehouses
                          .filter((w) => String(w.id) !== String(sourceWarehouseId))
                          .map((w) => (
                            <SelectItem key={`dest-${w.id}`} value={w.id}>
                              {w.name}
                            </SelectItem>
                          ))}
                        {loadingMoreWarehouses ? (
                          <div className="px-2 py-1.5 text-xs per-file-hint text-center">
                            {t("common.loading")}
                          </div>
                        ) : null}
                      </SelectContent>
                    </Select>
                    <FieldError message={showError("destinationWarehouseId")} />
                  </div>
                ) : null}

                {isVendorTransfer ? (
                  <>
                    <div className="space-y-1.5">
                      <FieldLabel required loading={loadingVendors}>
                        {t("inventory.vendorName")}
                      </FieldLabel>
                      <Select
                        value={vendorId}
                        onValueChange={setVendorId}
                        disabled={loadingVendors && vendors.length === 0}
                      >
                        <SelectTrigger className={selectTriggerClass}>
                          <SelectValue placeholder={t("inventory.selectVendor")} />
                        </SelectTrigger>
                        <SelectContent
                          className="z-[100]"
                          viewportClassName="h-auto max-h-60 overflow-y-auto"
                          onViewportScroll={handleVendorScroll}
                        >
                          {vendors.map((v) => (
                            <SelectItem key={`vendor-${v.id}`} value={v.id}>
                              {v.name}
                            </SelectItem>
                          ))}
                          {loadingMoreVendors ? (
                            <div className="px-2 py-1.5 text-xs per-file-hint text-center">
                              {t("common.loading")}
                            </div>
                          ) : null}
                        </SelectContent>
                      </Select>
                      {!loadingVendors && vendors.length === 0 ? (
                        <p className="text-[11px] text-rose-500 font-bold mt-1">
                          {t("inventory.noActiveVendors")}
                        </p>
                      ) : null}
                      <FieldError message={showError("vendorId")} />
                    </div>

                    <div className="space-y-1.5">
                      <FieldLabel loading={loadingVendorParty}>
                        {t("inventory.address")}
                      </FieldLabel>
                      <Select
                        value={vendorAddressId}
                        onValueChange={setVendorAddressId}
                        disabled={!vendorId || (loadingVendorParty && vendorAddresses.length === 0)}
                      >
                        <SelectTrigger className={selectTriggerClass}>
                          <SelectValue
                            placeholder={
                              !vendorId
                                ? t("inventory.selectVendorFirst")
                                : t("inventory.selectAddress")
                            }
                          />
                        </SelectTrigger>
                        <SelectContent
                          className="z-[100]"
                          viewportClassName="h-auto max-h-60 overflow-y-auto"
                        >
                          {vendorAddresses.map((row) => (
                            <SelectItem key={`vendor-address-${row.id}`} value={row.id}>
                              {row.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1.5">
                      <FieldLabel loading={loadingVendorParty}>
                        {t("inventory.mobileNumber")}
                      </FieldLabel>
                      <Select
                        value={vendorMobileId}
                        onValueChange={setVendorMobileId}
                        disabled={!vendorId || (loadingVendorParty && vendorMobiles.length === 0)}
                      >
                        <SelectTrigger className={selectTriggerClass}>
                          <SelectValue
                            placeholder={
                              !vendorId
                                ? t("inventory.selectVendorFirst")
                                : t("inventory.selectMobileNumber")
                            }
                          />
                        </SelectTrigger>
                        <SelectContent
                          className="z-[100]"
                          viewportClassName="h-auto max-h-60 overflow-y-auto"
                        >
                          {vendorMobiles.map((row) => (
                            <SelectItem key={`vendor-mobile-${row.id}`} value={row.id}>
                              {row.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                ) : null}

                {isCustomerTransfer ? (
                  <>
                    <div className="space-y-1.5">
                      <FieldLabel required loading={loadingCustomers}>
                        {t("inventory.customerName")}
                      </FieldLabel>
                      <Select
                        value={customerId}
                        onValueChange={setCustomerId}
                        disabled={loadingCustomers && customers.length === 0}
                      >
                        <SelectTrigger className={selectTriggerClass}>
                          <SelectValue placeholder={t("inventory.selectCustomer")} />
                        </SelectTrigger>
                        <SelectContent
                          className="z-[100]"
                          viewportClassName="h-auto max-h-60 overflow-y-auto"
                          onViewportScroll={handleCustomerScroll}
                        >
                          {customers.map((c) => (
                            <SelectItem key={`customer-${c.id}`} value={c.id}>
                              {c.name}
                            </SelectItem>
                          ))}
                          {loadingMoreCustomers ? (
                            <div className="px-2 py-1.5 text-xs per-file-hint text-center">
                              {t("common.loading")}
                            </div>
                          ) : null}
                        </SelectContent>
                      </Select>
                      <FieldError message={showError("customerId")} />
                    </div>

                    <div className="space-y-1.5">
                      <FieldLabel loading={loadingCustomerAddresses}>
                        {t("inventory.address")}
                      </FieldLabel>
                      <Select
                        value={customerAddressId}
                        onValueChange={setCustomerAddressId}
                        disabled={
                          !customerId ||
                          (loadingCustomerAddresses && customerAddresses.length === 0)
                        }
                      >
                        <SelectTrigger className={selectTriggerClass}>
                          <SelectValue
                            placeholder={
                              !customerId
                                ? t("inventory.selectCustomerFirst")
                                : t("inventory.selectAddress")
                            }
                          />
                        </SelectTrigger>
                        <SelectContent
                          className="z-[100]"
                          viewportClassName="h-auto max-h-60 overflow-y-auto"
                        >
                          {customerAddresses.map((row) => (
                            <SelectItem key={`customer-address-${row.id}`} value={row.id}>
                              {row.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1.5">
                      <FieldLabel loading={loadingCustomerMobiles}>
                        {t("inventory.mobileNumber")}
                      </FieldLabel>
                      <Select
                        value={customerMobileId}
                        onValueChange={setCustomerMobileId}
                        disabled={
                          !customerId ||
                          (loadingCustomerMobiles && customerMobiles.length === 0)
                        }
                      >
                        <SelectTrigger className={selectTriggerClass}>
                          <SelectValue
                            placeholder={
                              !customerId
                                ? t("inventory.selectCustomerFirst")
                                : t("inventory.selectMobileNumber")
                            }
                          />
                        </SelectTrigger>
                        <SelectContent
                          className="z-[100]"
                          viewportClassName="h-auto max-h-60 overflow-y-auto"
                        >
                          {customerMobiles.map((row) => (
                            <SelectItem key={`customer-mobile-${row.id}`} value={row.id}>
                              {row.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                ) : null}

                <div className="space-y-1.5">
                  <FieldLabel required>{t("formActions.transferQuantity")}</FieldLabel>
                  <input
                    type="number"
                    min="1"
                    step="any"
                    value={transferQty || ""}
                    onChange={(e) => setTransferQty(Math.max(0, Number(e.target.value) || 0))}
                    disabled={
                      !stockReady ||
                      (usesDefectiveQty &&
                        defectiveQtyNumber != null &&
                        defectiveQtyNumber <= 0)
                    }
                    placeholder="0"
                    className="w-full h-10 px-3 rounded-xl border border-(--color-text-border) bg-white text-xs font-semibold focus:border-accent focus:outline-hidden disabled:opacity-50"
                  />
                  <FieldError
                    message={
                      showError("transferQty") ||
                      (stockReady &&
                      maxTransferQty != null &&
                      transferQty > maxTransferQty &&
                      batchNumber
                        ? usesDefectiveQty
                          ? t("validation.transferQtyExceedsDefective")
                          : t("validation.transferQtyExceedsAvailable")
                        : "") ||
                      (batchStockError && batchNumber ? batchStockError : "")
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <FieldLabel required>{t("dateTime.transferDate")}</FieldLabel>
                  <input
                    type="date"
                    value={transferDate}
                    onChange={(e) => setTransferDate(e.target.value)}
                    className="w-full h-10 px-3 rounded-xl border border-(--color-text-border) bg-white text-xs font-semibold focus:border-accent focus:outline-hidden"
                  />
                  <FieldError message={showError("transferDate")} />
                </div>

                <div className="space-y-1.5">
                  <FieldLabel>{t("inventory.stockWarrantyType")}</FieldLabel>
                  <Select value={warrantyType} onValueChange={setWarrantyType}>
                    <SelectTrigger className={selectTriggerClass}>
                      <SelectValue placeholder={t("inventory.selectWarrantyType")} />
                    </SelectTrigger>
                    <SelectContent className="z-[100]">
                      {WARRANTY_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {t(option.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-(--color-text-border)/50 shrink-0">
              <button
                type="button"
                disabled={submitting}
                onClick={() => handleOpenChange(false)}
                className="h-10 px-5 rounded-xl border border-(--color-text-border) per-file-hint font-bold text-xs hover:bg-gray-50 transition cursor-pointer disabled:opacity-50"
              >
                {t("formActions.cancel")}
              </button>
              <button
                type="submit"
                disabled={submitting || loadingBatchStock}
                className="h-10 px-6 rounded-xl bg-primary hover:bg-accent text-white font-bold text-xs transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? t("inventory.saving") : t("formActions.saveTransfer")}
              </button>
            </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
